import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAppConfig } from 'hooks/useAppConfig';
import { localFlagOverrides } from './localOverrides';

/**
 * The effective flag set, merged from the least to the most specific source.
 * A later source wins for every flag it names — including one it names
 * `false`, which a truthiness check would mistake for absence:
 *
 * 1. LaunchDarkly: stage, demo and production, targeted per company. Empty
 *    where the client was never given a context (see `FeatureFlagProvider`).
 * 2. `featureFlags` on `/api/config`: dev environments, which never reach
 *    LaunchDarkly. Terraform bakes the same values into the API, and every
 *    tenant and user sees them alike. e2e drives this source too, by
 *    intercepting `/api/config` (`cy.updateFeatureFlags`).
 * 3. `NEXT_PUBLIC_FEATURE_FLAG_VALUES`: a developer's own `next dev`.
 *
 * Read flags through this (or `useFeatureFlag` for a boolean), never through
 * LaunchDarkly's `useFlags` directly: that hook only ever sees the first source.
 */
export function useFeatureFlags(): Record<string, unknown> {
  const launchDarklyFlags = useFlags();
  const fixedFlags = useAppConfig()?.featureFlags;

  return useMemo(
    () => ({ ...launchDarklyFlags, ...fixedFlags, ...localFlagOverrides }),
    [launchDarklyFlags, fixedFlags],
  );
}
